import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { UserAvatar } from "@/components/UserAvatar";
import { useDebounce } from "@/hooks/useDebounce";
import { cn } from "@/lib/utils";
import { userService } from "@/services/userService";
import type { PriorityFormValues } from "./priority-form-schema";
import { avatarAssigneeClass, avatarAssigneeFallbackClass } from "./priority-form-constants";

interface AssigneeBlockerFieldsProps {
    index: number;
    rowLocked: boolean;
    /** The assignee's own pid, excluded from the blocker candidate list — a person cannot block themselves. */
    rowUserPid: string;
    /** Blocker from server when editing, for label before search loads. */
    initialAssigneeBlocker?: { pid: string; fullName: string };
}

/**
 * Blocker picker + reason input shown once an assignee's status is set to Block Int, plus the
 * "will be notified" hint — split out of AssigneeRow to keep that file under the repo's line limit.
 */
export function AssigneeBlockerFields({ index, rowLocked, rowUserPid, initialAssigneeBlocker }: AssigneeBlockerFieldsProps) {
    const form = useFormContext<PriorityFormValues>();
    const [openAssigneeBlocker, setOpenAssigneeBlocker] = useState(false);
    const [assigneeBlockerSearch, setAssigneeBlockerSearch] = useState("");
    const debouncedAssigneeBlockerSearch = useDebounce(assigneeBlockerSearch, 300);

    const assigneeBlockerPid = useWatch({
        control: form.control,
        name: `assignments.${index}.blockerUserPid`,
    });

    const { data: assigneeBlockerUsersResponse, isLoading: isLoadingAssigneeBlockerUsers } = useQuery({
        queryKey: ["users-assignee-blocker", debouncedAssigneeBlockerSearch],
        queryFn: () => userService.getCompanyUsers(debouncedAssigneeBlockerSearch, 1, 20),
    });
    const assigneeBlockerUsers =
        assigneeBlockerUsersResponse?.data?.filter((user) => user.pid !== rowUserPid) ?? [];

    // Resolves the picked blocker's display name for the notify hint below — same fallback chain the
    // picker button uses, so the hint never shows a raw pid while the search results are still loading.
    const selectedBlockerName = assigneeBlockerPid
        ? assigneeBlockerUsers.find((usr) => usr.pid === assigneeBlockerPid)?.fullName ??
          (initialAssigneeBlocker?.pid === assigneeBlockerPid ? initialAssigneeBlocker.fullName : undefined)
        : undefined;

    return (
        <>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <FormField
                    control={form.control}
                    name={`assignments.${index}.blockerUserPid`}
                    render={({ field: bf }) => (
                        <FormItem>
                            <FormLabel className="text-[10px] uppercase text-muted-foreground">Blocked by *</FormLabel>
                            <Popover open={openAssigneeBlocker} onOpenChange={setOpenAssigneeBlocker}>
                                <PopoverTrigger asChild>
                                    <FormControl>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={openAssigneeBlocker}
                                            disabled={rowLocked}
                                            className={cn(
                                                "h-9 w-full justify-between bg-background text-left text-xs font-normal",
                                                !bf.value && "text-muted-foreground"
                                            )}
                                        >
                                            <span className="truncate">
                                                {bf.value
                                                    ? assigneeBlockerUsers.find((usr) => usr.pid === bf.value)?.fullName ||
                                                      (initialAssigneeBlocker?.pid === bf.value
                                                          ? initialAssigneeBlocker.fullName
                                                          : undefined) ||
                                                      "Selected"
                                                    : "Select blocker"}
                                            </span>
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </FormControl>
                                </PopoverTrigger>
                                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                                    <Command shouldFilter={false}>
                                        <CommandInput
                                            placeholder="Search user..."
                                            onValueChange={setAssigneeBlockerSearch}
                                            value={assigneeBlockerSearch}
                                        />
                                        <CommandList>
                                            <CommandEmpty>
                                                {isLoadingAssigneeBlockerUsers ? "Loading..." : "No user found."}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {assigneeBlockerUsers.map((user) => (
                                                    <CommandItem
                                                        key={user.pid}
                                                        value={user.fullName}
                                                        onSelect={() => {
                                                            bf.onChange(user.pid);
                                                            setOpenAssigneeBlocker(false);
                                                        }}
                                                    >
                                                        <Check
                                                            className={cn(
                                                                "mr-2 h-4 w-4",
                                                                bf.value === user.pid ? "opacity-100" : "opacity-0"
                                                            )}
                                                        />
                                                        <UserAvatar
                                                            src={user.avatarUrl}
                                                            name={user.fullName}
                                                            className={cn(avatarAssigneeClass, "mr-2")}
                                                            fallbackClassName={avatarAssigneeFallbackClass}
                                                        />
                                                        {user.fullName}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name={`assignments.${index}.blockedReason`}
                    render={({ field: br }) => (
                        <FormItem>
                            <FormLabel className="text-[10px] uppercase text-muted-foreground">Blocked reason *</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="What is blocked or waiting on?"
                                    className="h-9 text-xs"
                                    {...br}
                                    value={br.value ?? ""}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            {selectedBlockerName ? (
                <p className="mt-1.5 text-[11px] text-muted-foreground">
                    {selectedBlockerName} will be notified via OneThing assistant
                </p>
            ) : null}
        </>
    );
}
